import { permissionIds, type PermissionId } from '@dispatch/domain';
import { Alert, AppShell, BuildInformation, LoadingPanel, StatusPanel } from '@dispatch/ui';
import { useEffect, useMemo, useState } from 'react';
import { apiRequest, ApiRequestError, errorMessage } from './api.js';
import { AuthGate, readCsrfCookie } from './AuthGate.js';
import { BusinessSettingsPanel } from './BusinessSettingsPanel.js';
import { DevicesPanel } from './DevicesPanel.js';
import { PlatformPanel } from './PlatformPanel.js';
import { UsersPanel } from './UsersPanel.js';
import { TemplatesPanel } from './TemplatesPanel.js';
import { ReferenceDataPanel } from './ReferenceDataPanel.js';
import type { ActiveSupportAccess, BusinessSummary, MeResponse } from './types.js';

const environment = import.meta.env.MODE;
const version = import.meta.env['VITE_BUILD_VERSION'] ?? 'dev';

type Page =
  'dashboard' | 'settings' | 'users' | 'devices' | 'templates' | 'reference-data' | 'platform';

function loadStoredSupportAccess(): ActiveSupportAccess | undefined {
  const raw = sessionStorage.getItem('dispatch-support-access');
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as ActiveSupportAccess;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem('dispatch-support-access');
      return undefined;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem('dispatch-support-access');
    return undefined;
  }
}

export function App() {
  const [me, setMe] = useState<MeResponse>();
  const [csrfToken, setCsrfToken] = useState<string>();
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string>();
  const [page, setPage] = useState<Page>('dashboard');
  const [activeBusinessId, setActiveBusinessId] = useState<string>();
  const [switchingBusiness, setSwitchingBusiness] = useState(false);
  const [activeSupportAccess, setActiveSupportAccess] = useState<ActiveSupportAccess | undefined>(
    loadStoredSupportAccess,
  );

  async function loadMe() {
    try {
      const result = await apiRequest<MeResponse>('/api/v1/me');
      const csrf = readCsrfCookie();
      if (!csrf) {
        setMe(undefined);
        setCsrfToken(undefined);
        setSessionError('Sign in again to establish the protected browser session.');
        return;
      }
      setMe(result);
      setCsrfToken(csrf);
      setSessionError(undefined);
      const storedBusiness = result.session.activeBusinessId;
      if (storedBusiness && result.businesses.some((business) => business.id === storedBusiness)) {
        setActiveBusinessId(storedBusiness);
      } else if (result.businesses.length === 1) {
        setActiveBusinessId(result.businesses[0]?.id);
      }
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 401) {
        setMe(undefined);
        setCsrfToken(undefined);
      } else {
        setSessionError(errorMessage(caught));
      }
    } finally {
      setCheckingSession(false);
    }
  }

  useEffect(() => {
    void loadMe();
  }, []);

  async function selectBusiness(businessId: string) {
    if (!csrfToken) return;
    setSwitchingBusiness(true);
    setSessionError(undefined);
    try {
      await apiRequest('/api/v1/me/active-business', {
        method: 'POST',
        csrfToken,
        body: { businessId },
      });
      setActiveBusinessId(businessId);
      setPage('dashboard');
      await loadMe();
    } catch (caught) {
      setSessionError(errorMessage(caught));
    } finally {
      setSwitchingBusiness(false);
    }
  }

  async function logout() {
    if (!csrfToken) {
      setMe(undefined);
      return;
    }
    try {
      await apiRequest('/api/v1/auth/logout', { method: 'POST', csrfToken });
    } catch (caught) {
      setSessionError(errorMessage(caught));
    } finally {
      sessionStorage.removeItem('dispatch-support-access');
      setActiveSupportAccess(undefined);
      setMe(undefined);
      setCsrfToken(undefined);
      setActiveBusinessId(undefined);
      setPage('dashboard');
    }
  }

  const normalBusiness = useMemo(
    () => me?.businesses.find((business) => business.id === activeBusinessId),
    [activeBusinessId, me],
  );
  const effectiveBusinessId = activeSupportAccess?.businessId ?? normalBusiness?.id;
  const supportAccessId = activeSupportAccess?.id;
  const heldPermissions: readonly PermissionId[] = activeSupportAccess
    ? permissionIds
    : (normalBusiness?.permissionIds ?? []);
  const businessSuperadmin = Boolean(normalBusiness?.businessSuperadmin);
  const canAdministerBusinessSuperadmin = businessSuperadmin || Boolean(activeSupportAccess);
  const isPlatformSuperadmin = Boolean(me?.account.is_platform_superadmin);
  const has = (permissionId: PermissionId) =>
    businessSuperadmin || Boolean(activeSupportAccess) || heldPermissions.includes(permissionId);

  if (checkingSession) {
    return (
      <AppShell
        applicationName="Office Web"
        applicationDescription="Secure business administration, identity management, forms and reference-data administration."
        phaseLabel="Phase 4"
      >
        <LoadingPanel message="Checking your session…" />
      </AppShell>
    );
  }

  if (!me || !csrfToken) {
    return (
      <AppShell
        applicationName="Office Web"
        applicationDescription="Secure business administration, identity management, forms and reference-data administration."
        phaseLabel="Phase 4"
        footerText="Authentication uses a secure HTTP-only session and mandatory two-factor verification."
      >
        {sessionError ? <Alert title="Session notice">{sessionError}</Alert> : null}
        <AuthGate
          onAuthenticated={(token) => {
            setCsrfToken(token);
            setCheckingSession(true);
            void loadMe();
          }}
        />
      </AppShell>
    );
  }

  const navigation = [
    {
      href: '#dashboard',
      label: 'Dashboard',
      current: page === 'dashboard',
      onSelect: () => setPage('dashboard'),
    },
    ...(effectiveBusinessId
      ? [
          {
            href: '#settings',
            label: 'Business settings',
            current: page === 'settings',
            onSelect: () => setPage('settings'),
          },
          ...(has('users.manage')
            ? [
                {
                  href: '#users',
                  label: 'Users',
                  current: page === 'users',
                  onSelect: () => setPage('users'),
                },
              ]
            : []),
          {
            href: '#devices',
            label: 'Devices',
            current: page === 'devices',
            onSelect: () => setPage('devices'),
          },
          ...(has('templates.build') ||
          has('templates.publish') ||
          has('templates.retire') ||
          has('templates.folders_manage')
            ? [
                {
                  href: '#templates',
                  label: 'Forms',
                  current: page === 'templates',
                  onSelect: () => setPage('templates'),
                },
              ]
            : []),
          ...(has('reference.manage')
            ? [
                {
                  href: '#reference-data',
                  label: 'Reference data',
                  current: page === 'reference-data',
                  onSelect: () => setPage('reference-data'),
                },
              ]
            : []),
        ]
      : []),
    ...(isPlatformSuperadmin
      ? [
          {
            href: '#platform',
            label: 'Platform administration',
            current: page === 'platform',
            onSelect: () => setPage('platform'),
          },
        ]
      : []),
  ];

  return (
    <AppShell
      applicationName="Office Web"
      applicationDescription="Identity, business membership, permissions, devices, forms, reference data and audited Support Access."
      phaseLabel="Phase 4"
      navigation={navigation}
      accountLabel={`${me.account.display_name} · ${me.account.email}`}
      headerActions={
        <button type="button" onClick={() => void logout()}>
          Sign out
        </button>
      }
      footerText="Phase 4 Form Builder and runtime. PDF Builder, jobs and offline job sync remain unavailable until their approved phases."
    >
      {sessionError ? <Alert title="Request failed">{sessionError}</Alert> : null}
      {activeSupportAccess ? (
        <div className="support-banner" role="status">
          <strong>Support Access:</strong> {activeSupportAccess.businessName} until{' '}
          {new Date(activeSupportAccess.expiresAt).toLocaleString()}. Every action is audited.
        </div>
      ) : null}

      {page === 'dashboard' ? (
        <Dashboard
          me={me}
          activeBusiness={normalBusiness}
          activeSupportAccess={activeSupportAccess}
          switching={switchingBusiness}
          onSelectBusiness={(businessId) => void selectBusiness(businessId)}
          onOpenPlatform={() => setPage('platform')}
        />
      ) : null}

      {page === 'settings' && effectiveBusinessId ? (
        <BusinessSettingsPanel
          businessId={effectiveBusinessId}
          csrfToken={csrfToken}
          supportAccessId={supportAccessId}
          canEdit={has('business.manage_settings')}
        />
      ) : null}

      {page === 'users' && effectiveBusinessId ? (
        <UsersPanel
          businessId={effectiveBusinessId}
          csrfToken={csrfToken}
          supportAccessId={supportAccessId}
          canManageUsers={has('users.manage')}
          canDelegate={has('permissions.delegate')}
          canTransferSuperadmin={canAdministerBusinessSuperadmin}
          canAdministerBusinessSuperadmin={canAdministerBusinessSuperadmin}
          heldPermissionIds={heldPermissions}
          onSuperadminTransferred={(transfer) => {
            if (transfer.currentSessionRevoked) {
              sessionStorage.removeItem('dispatch-support-access');
              setActiveSupportAccess(undefined);
              setMe(undefined);
              setCsrfToken(undefined);
              setActiveBusinessId(undefined);
              setPage('dashboard');
              return;
            }

            setCheckingSession(true);
            setPage('users');
            void loadMe();
          }}
        />
      ) : null}

      {page === 'devices' && effectiveBusinessId ? (
        <DevicesPanel
          businessId={effectiveBusinessId}
          csrfToken={csrfToken}
          supportAccessId={supportAccessId}
        />
      ) : null}

      {page === 'templates' && effectiveBusinessId ? (
        <TemplatesPanel
          businessId={effectiveBusinessId}
          csrfToken={csrfToken}
          supportAccessId={supportAccessId}
          canBuild={has('templates.build')}
          canManageFolders={has('templates.folders_manage')}
        />
      ) : null}

      {page === 'reference-data' && effectiveBusinessId && has('reference.manage') ? (
        <ReferenceDataPanel
          businessId={effectiveBusinessId}
          csrfToken={csrfToken}
          supportAccessId={supportAccessId}
        />
      ) : null}

      {page === 'platform' && isPlatformSuperadmin ? (
        <PlatformPanel
          csrfToken={csrfToken}
          activeSupportAccess={activeSupportAccess}
          onSupportAccessChanged={(value) => {
            setActiveSupportAccess(value);
            if (value) setPage('dashboard');
          }}
        />
      ) : null}

      <BuildInformation application="Office Web" environment={environment} version={version} />
    </AppShell>
  );
}

function Dashboard({
  me,
  activeBusiness,
  activeSupportAccess,
  switching,
  onSelectBusiness,
  onOpenPlatform,
}: {
  me: MeResponse;
  activeBusiness?: BusinessSummary | undefined;
  activeSupportAccess?: ActiveSupportAccess | undefined;
  switching: boolean;
  onSelectBusiness: (businessId: string) => void;
  onOpenPlatform: () => void;
}) {
  return (
    <div>
      <StatusPanel heading="Identity status">
        <p>
          Signed in as <strong>{me.account.display_name}</strong>. Mandatory MFA was verified at{' '}
          {new Date(me.session.mfaVerifiedAt).toLocaleString()}.
        </p>
      </StatusPanel>

      {me.account.is_platform_superadmin ? (
        <section className="panel" aria-labelledby="platform-role-heading">
          <h2 id="platform-role-heading">Platform Superadmin</h2>
          <p>
            Tenant data is unavailable by default. Start explicit Support Access from Platform
            administration before viewing a business.
          </p>
          <button type="button" onClick={onOpenPlatform}>
            Open platform administration
          </button>
        </section>
      ) : null}

      {activeSupportAccess ? (
        <section className="panel" aria-labelledby="support-business-heading">
          <h2 id="support-business-heading">Support business context</h2>
          <dl className="definition-grid">
            <div>
              <dt>Business</dt>
              <dd>{activeSupportAccess.businessName}</dd>
            </div>
            <div>
              <dt>Reason</dt>
              <dd>{activeSupportAccess.reason}</dd>
            </div>
            <div>
              <dt>Expires</dt>
              <dd>{new Date(activeSupportAccess.expiresAt).toLocaleString()}</dd>
            </div>
          </dl>
        </section>
      ) : null}

      {!me.account.is_platform_superadmin ? (
        <section className="panel" aria-labelledby="business-context-heading">
          <h2 id="business-context-heading">Business context</h2>
          {me.businesses.length === 0 ? (
            <Alert tone="warning">This account has no active business membership.</Alert>
          ) : (
            <div className="card-grid">
              {me.businesses.map((business) => (
                <article
                  className={`card ${activeBusiness?.id === business.id ? 'card--selected' : ''}`}
                  key={business.id}
                >
                  <h3>{business.name}</h3>
                  <p>{business.slug}</p>
                  <p>
                    {business.businessSuperadmin
                      ? 'Business Superadmin'
                      : `${business.permissionIds.length} granted permissions`}
                  </p>
                  <button
                    type="button"
                    className={activeBusiness?.id === business.id ? 'secondary' : undefined}
                    disabled={switching || activeBusiness?.id === business.id}
                    onClick={() => onSelectBusiness(business.id)}
                  >
                    {activeBusiness?.id === business.id ? 'Current business' : 'Use this business'}
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
